import { useFormContext } from "react-hook-form";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import type { CrmRecordType } from "@/lib/api/crm/masters";
import type { CrmRules } from "@/lib/api/crm/rules";
import { addressTextFields, type RecordFormValues } from "../recordFormSchema";
import { TagPicker } from "./TagPicker";

/** Address inputs, in display order. */
const visibleAddressFields = [
  "label",
  "address_line_one",
  "address_line_two",
  "city",
  "state",
  "zipcode",
  "country",
  "county",
] as const satisfies readonly (typeof addressTextFields)[number][];

const addressFieldLabels: Record<(typeof visibleAddressFields)[number], string> = {
  label: "Label",
  address_line_one: "Line 1",
  address_line_two: "Line 2",
  city: "City",
  state: "State",
  zipcode: "Zip",
  country: "Country",
  county: "County",
};

const countryOptions = [
  { value: "USA", label: "USA" },
  { value: "CANADA", label: "CANADA" },
];

/** One block per address: every text field optional, tags (at least one) and delete. */
export function AddressesSection({
  rows,
  rules,
  recordType,
  notes,
  disabled,
  onAdd,
  onRemove,
  onTagsChange,
}: {
  /** useFieldArray rows; `key` is the row's stable React key. */
  rows: { key: string }[];
  rules: CrmRules;
  recordType: CrmRecordType | "";
  /** Notes about removed tags, by row key. */
  notes: Record<string, string>;
  disabled: boolean;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onTagsChange: (index: number, tagIds: string[]) => void;
}) {
  const form = useFormContext<RecordFormValues>();

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Addresses</h3>
        <Button type="button" variant="outline" size="sm" onClick={onAdd} disabled={disabled} data-testid="crm-form-add-address">
          <Plus className="mr-2 h-4 w-4" />
          Add address
        </Button>
      </div>
      {rows.length === 0 && <p className="text-sm text-gray-400">No addresses</p>}
      {rows.map((row, index) => {
        const rowError = form.formState.errors.addresses?.[index]?.id?.message;
        return (
          <div key={row.key} className="space-y-3 rounded-lg border bg-gray-50/60 p-3" data-testid={`crm-form-address-${index}`}>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {visibleAddressFields.map((name) => (
                <FormField
                  key={name}
                  control={form.control}
                  name={`addresses.${index}.${name}`}
                  render={({ field }) => (
                    <FormItem className={name === "address_line_one" || name === "address_line_two" ? "lg:col-span-2" : undefined}>
                      <FormLabel className="text-xs">{addressFieldLabels[name]}</FormLabel>
                      <FormControl>
                        {name === "country" ? (
                          <div>
                            <Combobox
                              isModal
                              dwimOnChange
                              className="w-full"
                              virtualizeList
                              popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
                              id={`crm-form-address-${index}-country`}
                              value={field.value}
                              onValueChange={field.onChange}
                              options={countryOptions}
                              disabled={disabled}
                            />
                          </div>
                        ) : (
                          <Input {...field} disabled={disabled} />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <div className="flex items-end gap-2">
              <FormField
                control={form.control}
                name={`addresses.${index}.tag_ids`}
                render={({ field }) => (
                  <FormItem className="min-w-0 flex-1">
                    <FormLabel className="text-xs">Tags *</FormLabel>
                    <FormControl>
                      <div>
                        {recordType ? (
                          <TagPicker
                            rules={rules}
                            target={{ type: "address", recordType }}
                            value={field.value}
                            onChange={(tagIds: string[]) => onTagsChange(index, tagIds)}
                            disabled={disabled}
                          />
                        ) : (
                          <p className="flex h-10 items-center text-sm text-gray-400">Choose a type first</p>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => onRemove(index)}
                disabled={disabled}
                aria-label="Remove address"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
            {notes[row.key] && <p className="text-xs text-amber-700">{notes[row.key]}</p>}
            {rowError && <p className="text-sm font-medium text-destructive" aria-invalid>{rowError}</p>}
          </div>
        );
      })}
    </section>
  );
}
